import { useCallback, useEffect, useState } from "react";
import { Badge, Collapse, Flex, Group, Select, Skeleton, Text } from "@mantine/core";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { getTerminalTelemetry } from "../../api/terminalApi";

/**
 * Terminal teşhis paneli.
 *
 * İKİ KATMAN — hedef kitleleri farklı:
 *
 *  1. ÖZET (herkese): "bu kapı çalışıyor mu, aramam gerekir mi" sorusunu
 *     cevaplayan 6 kart. Bayt / dBm / reset kodu gibi ham değerler burada
 *     GÖSTERİLMEZ; İK kullanıcısı bunlara göre bir karar veremez.
 *
 *  2. TEKNİK AYRINTILAR (yalnızca admin, katlanmış): dahili RAM zaman serisi ve
 *     ham sayaçlar. Teşhis kuralı firmware'deki health_monitor.h yorumuyla aynı:
 *       intFree sürekli düşüyorsa               -> sızıntı
 *       intFree sabit ama intBlock küçülüyorsa  -> parçalanma
 *       ikisi de sabit ama boots artıyorsa      -> C6 / SDIO hattı
 *       ikisi de sabit ama cpRec artıyorsa      -> C6 donuyor, firmware onarıyor
 *     Son satır 2026-08 sahada doğrulandı: C6 çalışırken RPC'lere yanıt vermeyi
 *     bırakabiliyor (BLE + Wi-Fi ölü, ekranda QR duruyor ama kapı açılmıyor).
 *     Firmware artık bunu algılayıp C6'yı P4'ü yeniden başlatmadan resetliyor;
 *     `cpRec` o olayın tek uzaktan kanıtı. `boots` sabitken artması "kapı
 *     kendini onardı" demektir — sessizce ölmesinden çok farklı bir durum.
 *     Tek bir anlık değer bu üçünü ayırt etmeye yetmediği için grafik zaman
 *     serisi gösterir.
 */

/** Telemetri 60 sn'de bir gelir; bu eşiklerin gerekçesi Tablets/Table.tsx'te. */
const STALE_AFTER_MS = 5 * 60 * 1000;
const CRITICAL_AFTER_MS = 20 * 60 * 1000;

/** Bellek dibi bu değerin altına inerse TLS/AES tahsisleri riske giriyor. */
const LOW_MEMORY_BYTES = 45 * 1024;

/** esp_reset_reason() -> İK'nın anlayacağı karşılık. */
const RESET_REASONS: Record<number, string> = {
  0: "Bilinmiyor",
  1: "Elektrik verildi",
  2: "Sıfırlama düğmesi",
  3: "Yazılım yeniden başlattı",
  4: "Beklenmedik hata",
  5: "Yanıt vermedi (kesme)",
  6: "Yanıt vermedi (görev)",
  7: "Yanıt vermedi",
  8: "Uykudan uyandı",
  9: "Elektrik dalgalanması",
  10: "Bağlantı birimi",
};

/** Çökme / watchdog / brownout: normal bir açılış değil. */
const isAbnormalReset = (r?: number) =>
  r !== undefined && [4, 5, 6, 7, 9].includes(r);

const formatBytes = (b?: number) => {
  if (b === undefined || b === null) return "—";
  if (b < 1024) return `${b} B`;
  return `${(b / 1024).toFixed(1)} KB`;
};

const formatUptime = (s?: number) => {
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d} gün ${h} saat`;
  if (h) return `${h} saat ${m} dk`;
  return `${m} dakika`;
};

const formatAgo = (iso?: string) => {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 90) return `${diff} saniye önce`;
  if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
};

/** RSSI (dBm) -> sözel kalite. İK'ya dBm göstermenin bir anlamı yok. */
const signalQuality = (rssi?: number) => {
  if (rssi === undefined || rssi === 0) return { label: "—", tone: "muted" as const };
  if (rssi >= -60) return { label: "Güçlü", tone: "good" as const };
  if (rssi >= -72) return { label: "Orta", tone: "warn" as const };
  return { label: "Zayıf", tone: "bad" as const };
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      {payload.map((p: any, i: number) => (
        <TooltipRow key={i}>
          <TooltipDot $color={p.color} />
          <span>{p.name}</span>
          <strong>{formatBytes(p.value)}</strong>
        </TooltipRow>
      ))}
    </TooltipBox>
  );
};

const DiagnosticsPanel = ({
  terminalId,
  isAdmin = false,
}: {
  terminalId?: string;
  isAdmin?: boolean;
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState<string>("24");
  const [techOpen, setTechOpen] = useState(false);

  const fetchSeries = useCallback(async () => {
    if (!terminalId) return;
    setLoading(true);
    try {
      const res: any = await getTerminalTelemetry({
        terminalId,
        hours: Number(hours),
      });
      setRows(res?.data?.success ? res.data.data || [] : []);
    } catch (error) {
      console.error("Telemetri yüklenemedi", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [terminalId, hours]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  const latest = rows.length ? rows[rows.length - 1] : null;

  if (loading) {
    return (
      <Flex direction="column" gap={16}>
        <Skeleton height={100} radius="md" />
        <Skeleton height={100} radius="md" />
      </Flex>
    );
  }

  if (!rows.length) {
    return (
      <EmptyBox>
        <Text fw={500} size="md" c="#111827">
          Henüz bilgi yok
        </Text>
        <Text size="sm" c="#6b7280" mt={6}>
          Bu kapı dakikada bir durum bilgisi gönderir. Kapı kapalıysa, internete
          bağlı değilse veya yeni kurulduysa burası bir süre boş kalır.
        </Text>
      </EmptyBox>
    );
  }

  // ─── Özet değerler ──────────────────────────────────────────────────────
  const ageMs = latest?.at ? Date.now() - new Date(latest.at).getTime() : Infinity;
  const online = ageMs <= STALE_AFTER_MS;
  const signal = signalQuality(latest?.rssi);

  // Sürüm: fwLatest boşsa cihaz sunucuya henüz sormamış demektir, "güncel değil"
  // diye göstermek yanlış olur.
  const fw = latest?.fw || "—";
  const fwLatest = latest?.fwLatest || "";
  const updateAvailable = !!fwLatest && !!latest?.fw && fwLatest !== latest.fw;

  // Son 24 saatte kaç kez beklenmedik şekilde yeniden başladı. `boots` monoton
  // artan bir sayaç olduğu için serideki ilk/son fark, penceredeki yeniden
  // başlatma sayısını verir.
  const firstBoots = rows.find((r) => r.boots !== undefined)?.boots;
  const restarts =
    latest?.boots !== undefined && firstBoots !== undefined
      ? Math.max(0, latest.boots - firstBoots)
      : 0;

  // C6 (bağlantı birimi) kendini kaç kez toparladı. `cpRec` de `boots` gibi
  // monoton ve kalıcı olduğu için serideki ilk/son farkı pencere içindeki
  // kurtarma sayısını verir.
  //
  // NEDEN ÖNEMLİ: bu sayaç artarken `boots` sabit kalıyorsa, kapı BLE/Wi-Fi'yi
  // kaybediyor ama firmware onu yeniden başlatmadan onarıyor demektir — yani
  // kullanıcı çoğu zaman hiçbir şey fark etmiyor. Sessizce ölen bir terminalden
  // (eski davranış: 13 saat boyunca QR gösterip kapıyı açmamak) tamamen farklı
  // bir durum ve karışmaması gerekiyor.
  const firstRec = rows.find((r) => r.cpRec !== undefined)?.cpRec;
  const recoveries =
    latest?.cpRec !== undefined && firstRec !== undefined
      ? Math.max(0, latest.cpRec - firstRec)
      : 0;

  // Son kurtarmanın üzerinden geçen süre: cpRecAt aynı açılışın uptime'ı,
  // dolayısıyla fark ancak `up` ondan büyükse anlamlı.
  const sinceRecoveryS =
    latest?.cpRecAt && latest?.up && latest.up >= latest.cpRecAt
      ? latest.up - latest.cpRecAt
      : undefined;

  const lowMemory =
    latest?.minIntFree !== undefined && latest.minIntFree < LOW_MEMORY_BYTES;

  // ─── Genel sağlık: üç sinyalin birleşimi ────────────────────────────────
  // İK'nın tek bakışta "aramam gerekir mi" sorusunu cevaplaması için.
  let health: { label: string; tone: "good" | "warn" | "bad"; note: string };
  if (ageMs > CRITICAL_AFTER_MS) {
    health = {
      label: "Sorunlu",
      tone: "bad",
      note: "Kapı uzun süredir haber vermiyor. Elektrik ve internet bağlantısını kontrol ettirin.",
    };
  } else if (restarts >= 3) {
    health = {
      label: "Sorunlu",
      tone: "bad",
      note: `Son ${hours} saatte ${restarts} kez yeniden başladı. Teknik destekle görüşün.`,
    };
  } else if (recoveries >= 3) {
    // Kapı çalışmaya devam ediyor ama bağlantı birimi tekrar tekrar donuyor —
    // kendini onarması bir çözüm değil, kronikleşmiş bir arızanın belirtisi.
    health = {
      label: "Sorunlu",
      tone: "bad",
      note: `Son ${hours} saatte bağlantı biriminde ${recoveries} kez sorun çıktı ve kapı kendini onardı. Kalıcı çözüm için teknik destekle görüşün.`,
    };
  } else if (
    !online ||
    restarts > 0 ||
    recoveries > 0 ||
    lowMemory ||
    signal.tone === "bad"
  ) {
    health = {
      label: "İzlenmeli",
      tone: "warn",
      note: !online
        ? "Son bilgi biraz eski; bağlantı dalgalı olabilir."
        : restarts > 0
        ? "Beklenmedik bir yeniden başlatma oldu. Tekrarlarsa bildirin."
        : recoveries > 0
        ? "Kapının bağlantı biriminde geçici bir sorun çıktı, kapı kendini onardı. Sık tekrarlarsa bildirin."
        : signal.tone === "bad"
        ? "Wi-Fi sinyali zayıf. Kapı zaman zaman bağlantı kaybedebilir."
        : "Cihaz kaynakları zorlanıyor. Şimdilik çalışıyor, izlemekte fayda var.",
    };
  } else {
    health = {
      label: "İyi",
      tone: "good",
      note: "Kapı sorunsuz çalışıyor.",
    };
  }

  // ─── Teknik grafik verisi (yalnızca admin) ──────────────────────────────
  const chartData = rows.map((r) => ({
    t: new Date(r.at).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    "Dahili boş": r.intFree,
    "En büyük blok": r.intBlock,
  }));

  return (
    <Flex direction="column" gap={20}>
      <StatRow>
        <Stat
          label="Durum"
          value={online ? "Çevrimiçi" : "Çevrimdışı"}
          tone={online ? "good" : "bad"}
          dot
        />
        <Stat label="Son bilgi" value={formatAgo(latest?.at)} />
        <Stat label="Kesintisiz çalışma" value={formatUptime(latest?.up)} />
        <Stat
          label="Wi-Fi sinyali"
          value={signal.label}
          tone={signal.tone === "muted" ? undefined : signal.tone}
        />
        <Stat
          label="Yazılım sürümü"
          value={fw}
          badge={
            updateAvailable
              ? { text: `${fwLatest} mevcut`, color: "orange" }
              : fwLatest
              ? { text: "Güncel", color: "teal" }
              : undefined
          }
        />
        <Stat
          label={`Yeniden başlatma (${hours} sa)`}
          value={restarts === 0 ? "Yok" : `${restarts} kez`}
          tone={restarts >= 3 ? "bad" : restarts > 0 ? "warn" : undefined}
        />
      </StatRow>

      <HealthBox $tone={health.tone}>
        <Group gap={10} align="center">
          <Text fw={600} size="md" c="#111827">
            Genel durum:
          </Text>
          <Badge
            size="lg"
            variant="light"
            color={
              health.tone === "good" ? "teal" : health.tone === "warn" ? "orange" : "red"
            }
          >
            {health.label}
          </Badge>
        </Group>
        <Text size="sm" c="#4b5563" mt={8}>
          {health.note}
        </Text>
        {isAbnormalReset(latest?.reset) && (
          <Text size="sm" c="#6b7280" mt={4}>
            Son açılış nedeni: {RESET_REASONS[latest.reset]}
          </Text>
        )}
      </HealthBox>

      <Flex justify="space-between" align="center">
        <Select
          value={hours}
          onChange={(v) => setHours(v || "24")}
          data={[
            { value: "6", label: "Son 6 saat" },
            { value: "24", label: "Son 24 saat" },
            { value: "72", label: "Son 3 gün" },
            { value: "168", label: "Son 7 gün" },
          ]}
          w={150}
          allowDeselect={false}
        />
        {isAdmin && (
          <TechToggle type="button" onClick={() => setTechOpen((o) => !o)}>
            {techOpen ? "▾" : "▸"} Teknik ayrıntılar
          </TechToggle>
        )}
      </Flex>

      {/* Teknik bölüm yalnızca admin'e: heap eğrisi ve ham sayaçlar İK için
          yorumlanabilir bilgi değil, ama saha teşhisi bunlar olmadan yapılamıyor. */}
      {isAdmin && (
        <Collapse in={techOpen}>
          <Flex direction="column" gap={16}>
            <ChartBox>
              <Text fw={600} size="sm" c="#111827" mb={8} ml={16}>
                Dahili RAM seyri
              </Text>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="t"
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    tickFormatter={(v) => `${Math.round(v / 1024)}K`}
                    width={52}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Line
                    type="monotone"
                    dataKey="Dahili boş"
                    stroke="#5c26ff"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="En büyük blok"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartBox>

            <RawGrid>
              <Raw label="Dahili boş (anlık)" value={formatBytes(latest?.intFree)} />
              <Raw label="Dahili boş (dip)" value={formatBytes(latest?.minIntFree)} />
              <Raw label="En büyük blok" value={formatBytes(latest?.intBlock)} />
              <Raw label="En büyük blok (dip)" value={formatBytes(latest?.minIntBlock)} />
              <Raw
                label="Sinyal"
                value={latest?.rssi ? `${latest.rssi} dBm` : "—"}
              />
              <Raw label="Toplam açılış" value={latest?.boots ?? "—"} />
              <Raw
                label="Son açılış nedeni"
                value={RESET_REASONS[latest?.reset] ?? `Kod ${latest?.reset ?? "—"}`}
              />
              <Raw label="C6 yardımcı işlemci" value={latest?.cp || "—"} />
              <Raw
                label="C6 kurtarma (toplam)"
                value={latest?.cpRec ?? "—"}
                tone={recoveries > 0 ? "warn" : undefined}
              />
              <Raw
                label="Son C6 kurtarma"
                value={
                  sinceRecoveryS === undefined
                    ? "Yok"
                    : `${formatUptime(sinceRecoveryS)} önce`
                }
              />
            </RawGrid>

            <HintBox>
              <Text size="sm" c="#4b5563">
                <strong>Nasıl okunur:</strong> “Dahili boş” sürekli düşüyorsa{" "}
                <Badge size="sm" color="red" variant="light">
                  bellek sızıntısı
                </Badge>
                . “Dahili boş” sabit ama “En büyük blok” küçülüyorsa{" "}
                <Badge size="sm" color="orange" variant="light">
                  parçalanma
                </Badge>
                . İkisi de sabitken “Toplam açılış” artıyorsa sorun bellekte
                değil,{" "}
                <Badge size="sm" color="grape" variant="light">
                  C6 / SDIO hattında
                </Badge>
                .
              </Text>
            </HintBox>
          </Flex>
        </Collapse>
      )}
    </Flex>
  );
};

const Stat = ({
  label,
  value,
  tone,
  dot,
  badge,
}: {
  label: string;
  value: string;
  tone?: "good" | "warn" | "bad";
  dot?: boolean;
  badge?: { text: string; color: string };
}) => {
  const color =
    tone === "bad" ? "#dc2626" : tone === "warn" ? "#d97706" : tone === "good" ? "#0f766e" : "#111827";
  return (
    <StatBox $tone={tone}>
      <Text size="xs" c="#6b7280" fw={500}>
        {label}
      </Text>
      <Group gap={8} align="center" mt={6} wrap="nowrap">
        {dot && <Dot $color={color} />}
        <Text size="lg" fw={600} c={color} style={{ lineHeight: 1.3 }}>
          {value}
        </Text>
      </Group>
      {badge && (
        <Badge size="xs" color={badge.color} variant="light" mt={6}>
          {badge.text}
        </Badge>
      )}
    </StatBox>
  );
};

const Raw = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: any;
  tone?: "good" | "warn" | "bad";
}) => (
  <Flex direction="column" gap={2}>
    <Text size="xs" c="#9ca3af">
      {label}
    </Text>
    <Text
      size="sm"
      fw={500}
      /* Stat ile aynı palet - iki bölüm arasında renk anlamı kaymasın. */
      c={
        tone === "bad"
          ? "#dc2626"
          : tone === "warn"
          ? "#d97706"
          : tone === "good"
          ? "#0f766e"
          : "#374151"
      }
    >
      {value}
    </Text>
  </Flex>
);

export default DiagnosticsPanel;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const StatBox = styled.div<{ $tone?: "good" | "warn" | "bad" }>`
  background: #ffffff;
  border: 1px solid
    ${(p) =>
      p.$tone === "bad" ? "#fecaca" : p.$tone === "warn" ? "#fed7aa" : "#e5e7eb"};
  border-radius: 12px;
  padding: 14px 16px;
`;

const Dot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex: none;
`;

const HealthBox = styled.div<{ $tone: "good" | "warn" | "bad" }>`
  background: ${(p) =>
    p.$tone === "bad" ? "#fef2f2" : p.$tone === "warn" ? "#fffbeb" : "#f0fdfa"};
  border: 1px solid
    ${(p) =>
      p.$tone === "bad" ? "#fecaca" : p.$tone === "warn" ? "#fed7aa" : "#99f6e4"};
  border-radius: 12px;
  padding: 16px;
`;

const TechToggle = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 4px;
  &:hover {
    color: #5c26ff;
  }
`;

const ChartBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 8px 8px 0;
`;

const RawGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
`;

const HintBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
`;

const EmptyBox = styled.div`
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
`;

const TooltipBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const TooltipLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  strong {
    margin-left: auto;
    color: #111827;
  }
`;

const TooltipDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`;
